// @story HIR-TC1
// My interviews — the candidates the signed-in person is on the interview
// panel for, soonest booked slot first, and whether they have submitted marks.
// Laid out as the other EquaOS apps are: a header, then one table; a row
// opens that candidate's marking panel.
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Card, EmptyState, Icon, StatusPill, Table } from '../ds';
import { Page, PageHeader, Section, Loading, Note } from '../components/page';

function when(slot) {
  if (!slot?.startsAt) return null;
  const d = new Date(slot.startsAt);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
}

const panelPath = (r) => `/jobs/${r.jobId}/human-interview/${r.stageId}/panel/${r.resumeId}`;

export default function MyInterviews() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/human-interview/mine')
      .then((r) => setRows(r.data?.data || []))
      .catch(() => { setRows([]); setError('Could not load your interviews just now.'); });
  }, []);

  const header = (
    <PageHeader
      title="My interviews"
      subtitle="Candidates you are on the interview panel for. Your marks are your own: you will see the rest of the panel's marks once you have submitted yours."
    />
  );

  if (rows === null) {
    return <Page>{header}<Card><Loading label="Loading your interviews" /></Card></Page>;
  }

  const columns = [
    { key: 'candidate', label: 'Candidate', render: (r) => (
      <div>
        <Link to={panelPath(r)} onClick={(e) => e.stopPropagation()} style={{ fontWeight: 500, color: 'var(--text-1)', textDecoration: 'none' }}>
          {r.candidateName}
        </Link>
        <div style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-caption)' }}>{r.jobTitle} · {r.stageLabel}</div>
      </div>
    ) },
    { key: 'when', label: 'Interview', render: (r) => (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <Icon name="calendar" />
        {when(r.slot) || 'No interview time booked yet'}
        {r.slot?.location && (<><Icon name="map-pin-area" style={{ marginLeft: 8 }} />{r.slot.location}</>)}
      </span>
    ) },
    { key: 'status', label: 'Marks', render: (r) => (r.status === 'submitted'
      ? <StatusPill tone="success">Marks submitted</StatusPill>
      : <StatusPill tone="warning">To mark</StatusPill>) },
  ];

  return (
    <Page>
      {header}
      {error && <Note tone="danger">{error}</Note>}
      {rows.length === 0 && !error && (
        <Card>
          <EmptyState icon={<Icon name="clipboard-text" size={36} />} title="No interviews"
            description="You are not on any interview panel at the moment." />
        </Card>
      )}
      {rows.length > 0 && (
        <Section title="Your panels" subtitle={`${rows.length} candidate${rows.length === 1 ? '' : 's'} · open one to mark`} padding="20px 0 8px">
          <Table columns={columns} rows={rows} getRowId={(r) => `${r.resumeId}:${r.stageId}`} onRowClick={(r) => navigate(panelPath(r))} />
        </Section>
      )}
    </Page>
  );
}
